import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import type { SubscriberListRef } from '@/features/subscribers';
import { PersonAvatar } from '@/shared/components/PersonAvatar';
import { pluralise } from '@/shared/lib/format';

const FACES = 3;

/** Who the message lands with: a few faces, the opted-in count, and their names and masked numbers on View. */
export function Recipients({ list }: { list: SubscriberListRef | undefined }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!list) return null;
  if (list.optedInCount === 0) {
    return <Typography variant="body2" sx={{ color: 'warning.main' }}>No opted-in numbers on this list.</Typography>;
  }
  const more = list.optedInCount - list.recipients.length;
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
      <Stack direction="row" sx={{ '& > * + *': { ml: -0.75 } }} aria-hidden>
        {list.recipients.slice(0, FACES).map((r) => <PersonAvatar key={`${r.name}-${r.phoneMasked}`} name={r.name} size={22} ring />)}
      </Stack>
      <Typography variant="body2">{pluralise(list.optedInCount, 'opted-in number')}</Typography>
      {list.recipients.length > 0 ? (
        <Link component="button" type="button" variant="body2" underline="hover" onClick={(e) => { setAnchor(e.currentTarget); }} sx={{ fontWeight: 500 }}>View</Link>
      ) : null}
      <Popover open={anchor !== null} anchorEl={anchor} onClose={() => { setAnchor(null); }} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 1.5, minWidth: 260 } } }}>
        <Stack component="ul" aria-label={`Recipients on ${list.name}`} sx={{ listStyle: 'none', m: 0, p: 1 }}>
          {list.recipients.map((r) => (
            <Stack component="li" direction="row" key={`${r.name}-${r.phoneMasked}`} sx={{ alignItems: 'center', gap: 1, px: 1, py: 0.75 }}>
              <PersonAvatar name={r.name} size={22} />
              <Typography variant="body2" sx={{ flexGrow: 1 }}>{r.name}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{r.phoneMasked}</Typography>
            </Stack>
          ))}
          {more > 0 ? <Box component="li" sx={{ px: 1, py: 0.75 }}><Typography variant="caption" sx={{ color: 'text.secondary' }}>and {more} more</Typography></Box> : null}
        </Stack>
      </Popover>
    </Stack>
  );
}
